/**
 * Validates a font path for safe CSS interpolation.
 * Rejects paths that don't start with `/` or `https://`, and paths
 * containing single quotes (which could break out of CSS strings).
 *
 * Used by WhiteLabelFontLoader (@font-face src) and WhiteLabelHero (font-family).
 */
export function sanitizeFontPath(path: string): string | null {
  if (!/^(\/|https:\/\/)/.test(path)) return null;
  if (path.includes("'")) return null;
  return path;
}

/**
 * Derives a CSS font-family name from a font file path.
 * Strips the directory prefix and file extension.
 *
 * Used by both WhiteLabelFontLoader (@font-face declarations)
 * and WhiteLabelHero (font-family on hero title) — must produce
 * identical output for the font cascade to work.
 *
 * @example fontFamilyFromPath('/fonts/Sentient-Regular.otf') => 'Sentient-Regular'
 */
export function fontFamilyFromPath(
  filePath: string,
  fallback = 'CustomFont'
): string {
  const filename = filePath.split('/').pop() ?? '';
  const nameWithoutExt = filename.replace(/\.[^.]+$/, '');
  if (!nameWithoutExt) return fallback;
  // Strip characters that could break out of a CSS quoted string context
  const safe = nameWithoutExt.replace(/[^a-zA-Z0-9 _-]/g, '');
  return safe || fallback;
}
